import { type ReactElement } from "react";
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";

import { DYNAMIC_FOOTNOTE_ATTRIBUTE } from "@vega-core/components/HtmlContent/DynamicFootnote";

import { FootnoteNodeView } from "./FootnoteNodeView";

export type SerializedFootnoteNode = Spread<
  { content: string },
  SerializedLexicalNode
>;

const convertFootnoteElement = (element: HTMLElement) =>
  element.hasAttribute(DYNAMIC_FOOTNOTE_ATTRIBUTE)
    ? { node: $createFootnoteNode(element.innerHTML) }
    : null;

export class FootnoteNode extends DecoratorNode<ReactElement> {
  __content: string;

  constructor(content: string, key?: NodeKey) {
    super(key);
    this.__content = content;
  }

  static getType(): string {
    return "footnote";
  }

  static clone(node: FootnoteNode): FootnoteNode {
    return new FootnoteNode(node.__content, node.__key);
  }

  static importJSON(serialized: SerializedFootnoteNode): FootnoteNode {
    return $createFootnoteNode(serialized.content);
  }

  static importDOM(): DOMConversionMap | null {
    const tryConvert = (domNode: HTMLElement) =>
      domNode.hasAttribute(DYNAMIC_FOOTNOTE_ATTRIBUTE)
        ? { conversion: convertFootnoteElement, priority: 4 as const }
        : null;

    return { a: tryConvert, span: tryConvert };
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "inline-block";

    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute(DYNAMIC_FOOTNOTE_ATTRIBUTE, "0");
    element.innerHTML = this.__content;

    return { element };
  }

  exportJSON(): SerializedFootnoteNode {
    return {
      ...super.exportJSON(),
      content: this.__content,
    };
  }

  isInline(): true {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  getContent(): string {
    return this.getLatest().__content;
  }

  setContent(content: string): void {
    this.getWritable().__content = content;
  }

  decorate(): ReactElement {
    return <FootnoteNodeView content={this.__content} nodeKey={this.__key} />;
  }
}

export function $createFootnoteNode(content: string): FootnoteNode {
  return $applyNodeReplacement(new FootnoteNode(content));
}

export function $isFootnoteNode(
  node: LexicalNode | null | undefined,
): node is FootnoteNode {
  return node instanceof FootnoteNode;
}
